import { Box, Grid, useMediaQuery, Typography } from '@material-ui/core';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../Session';
import {
    FullPurchaseTransactionDataFragment,
    PurchaseTransactionStage,
    UserType,
    VehicleDiagramComment,
    VehicleDiagramCommentPositionFields,
} from '../../../api';
import usePublic from '../../../utilities/usePublic';
import AddCommentDialog from './AddCommentDialog';
import Marker from './Marker';
import VehicleComment from './VehicleComment';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(10),
                padding: theme.spacing(3),
                paddingRight: theme.spacing(4),
            },
        },
        parent: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        commentsWrapper: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            marginLeft: theme.spacing(1),
        },
        comments: {
            marginTop: theme.spacing(2),
        },
        vehicle: {
            height: '367px',
            marginBottom: theme.spacing(2),
        },
        imageContainer: {
            position: 'relative',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '367px',
            cursor: 'pointer',
        },
        backgroundImage: {
            position: 'absolute',
            top: 0,
            left: 0,
            maxWidth: '600px',
            width: '100%',
            backgroundSize: 'cover',
            height: '367px',
            cursor: 'pointer',
        },
        markerWrapper: {
            position: 'absolute',
            width: '100%',
            maxWidth: '600px',
            height: '367px',
            clear: 'both',
        },
    })
);

export type VehicleDiagramBodyProps = {
    comments: VehicleDiagramComment[];
    purchaseTransaction: FullPurchaseTransactionDataFragment;
};

const VehicleDiagramBody = ({ comments, purchaseTransaction }: VehicleDiagramBodyProps) => {
    const [openAddCommentDialog, setOpenAddCommnetDialog] = React.useState(false);

    const currentUser = useCurrentUser();

    const [position, setPosition] = React.useState<VehicleDiagramCommentPositionFields>({ top: 0, left: 0 });

    const { t } = useTranslation(['purchaseTransactionsPage']);
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const vehicle = usePublic('assets/images/vehicleDiagram.png');

    const styles = useStyles();

    const vehicleElementRef = React.useRef(null);

    const onAddCommentClick = useCallback(
        event => {
            if (
                (purchaseTransaction.stage !== PurchaseTransactionStage.Completed &&
                    purchaseTransaction.stage !== PurchaseTransactionStage.Cancelled) ||
                currentUser.type === UserType.Admin ||
                currentUser.type === UserType.ValuationTeam
            ) {
                const parent = vehicleElementRef.current.getClientRects()[0];

                const left =
                    Math.round((((event.clientX - parent.x) / parent.width) * 100 + Number.EPSILON) * 100) / 100;
                const top = Math.round(((event.clientY - parent.y) / 3.67 + Number.EPSILON) * 100) / 100;

                setPosition({ left, top });
                setOpenAddCommnetDialog(true);
            }
        },
        [currentUser.type, purchaseTransaction.stage]
    );

    return (
        <Box className={styles.root} mb={isSmall ? 10 : 0}>
            <Grid className={styles.parent} spacing={1} container>
                <Grid
                    className={styles.vehicle}
                    lg={4}
                    md={5}
                    onClick={event => onAddCommentClick(event)}
                    sm={12}
                    xs={12}
                    container
                    item
                >
                    <Grid className={styles.imageContainer} item>
                        <img ref={vehicleElementRef} alt="" className={styles.backgroundImage} src={vehicle} />
                        <Box className={styles.markerWrapper}>
                            {comments &&
                                comments.length > 0 &&
                                comments.map(comment => (
                                    <Marker
                                        key={comment.id}
                                        index={comment.index}
                                        left={comment.position.left}
                                        top={comment.position.top}
                                        isSmall
                                    />
                                ))}
                        </Box>
                    </Grid>
                </Grid>

                <Grid className={styles.commentsWrapper} item>
                    <Box>
                        <Typography>{t('purchaseTransactionsPage:vehicleDiagram.commentHeader')}</Typography>
                    </Box>
                    <Grid className={styles.comments} item>
                        {comments &&
                            comments.length > 0 &&
                            comments.map(comment => (
                                <VehicleComment
                                    key={comment.id}
                                    comment={comment}
                                    purchaseTransaction={purchaseTransaction}
                                />
                            ))}
                    </Grid>
                </Grid>
            </Grid>
            <AddCommentDialog
                onClose={() => setOpenAddCommnetDialog(false)}
                open={openAddCommentDialog}
                position={position}
                purchaseTransaction={purchaseTransaction}
            />
        </Box>
    );
};

export default VehicleDiagramBody;
